/**
 * Happy Hounds Feeding Calculator
 * Vanilla JS — no jQuery dependency
 * Reads feeding data from CSV URLs stored in theme settings (output as inline JSON via snippet)
 * Supports both standard and insect protein variants via data-calculator-mode attribute
 */

(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────────────
  let puppyData = null;   // 2D lookup: puppyData[weightKg][monthIndex]
  let adultData = null;   // 2D lookup: adultData[weightKg]['Active'|'Indoor'|'HighlyActive']
  let beefPrices = {};    // { '250': 41.50, '500': 69.50, '1000': 123.50 }
  let nonBeefPrices = {}; // { '250': 39.50, ... }

  // ─── CSV parser ───────────────────────────────────────────────────────────
  /**
   * Parse puppy CSV into a nested object keyed by weight (1–90) then month (1–16).
   * Row index = weight (kg), first row = month headers.
   */
  function parsePuppyCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    const months = lines[0].split(',').map(Number); // [1,2,3,...,16]
    const result = {};
    lines.slice(1).forEach((line, rowIdx) => {
      const weight = rowIdx + 1; // 1-indexed weight in kg
      const values = line.split(',').map(Number);
      result[weight] = {};
      months.forEach((month, colIdx) => {
        result[weight][month] = values[colIdx];
      });
    });
    return result;
  }

  /**
   * Parse adult CSV into a nested object keyed by weight (1–90) then activity level.
   * First row = activity headers (Active, Indoor, HighlyActive).
   */
  function parseAdultCSV(text) {
    const lines = text.trim().split(/\r?\n/);
    const activities = lines[0].split(','); // ['Active','Indoor','HighlyActive']
    const result = {};
    lines.slice(1).forEach((line, rowIdx) => {
      const weight = rowIdx + 1;
      const values = line.split(',').map(Number);
      result[weight] = {};
      activities.forEach((activity, colIdx) => {
        // Normalise: strip whitespace, handle 'Lowactive' legacy variant
        const key = activity.trim().replace(/\s+/g, '').replace(/Lowactive/i, 'Indoor');
        result[weight][key] = values[colIdx];
      });
    });
    return result;
  }

  // ─── Fetch helpers ────────────────────────────────────────────────────────
  async function fetchCSV(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to load feeding guide data: ' + url);
    return response.text();
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  /**
   * Called once DOM is ready. Finds all calculator containers and initialises each.
   * Config is read from window.HH_CALC_CONFIG (set by the Liquid snippet).
   */
  async function init() {
    const containers = document.querySelectorAll('.calculator-container');
    if (!containers.length) return;

    const config = window.HH_CALC_CONFIG;
    if (!config) {
      console.error('HH_CALC_CONFIG not found. Check calculator-config snippet.');
      return;
    }

    // Determine which config set to use based on mode attribute
    containers.forEach(async (container) => {
      const mode = container.dataset.calculatorMode || 'standard';
      const cfg = mode === 'insect' ? config.insect : config.standard;

      if (!cfg || !cfg.puppyCsvUrl || !cfg.adultCsvUrl) {
        console.error('Missing CSV URLs in HH_CALC_CONFIG for mode:', mode);
        return;
      }

      // Parse tub prices from config
      const localBeefPrices = {};
      const localNonBeefPrices = {};
      if (cfg.tubPrices) {
        cfg.tubPrices.forEach(function(tub) {
          localBeefPrices[String(tub.size)] = parseFloat(tub.beefPrice);
          localNonBeefPrices[String(tub.size)] = parseFloat(tub.nonBeefPrice);
        });
      }

      try {
        const [puppyText, adultText] = await Promise.all([
          fetchCSV(cfg.puppyCsvUrl),
          fetchCSV(cfg.adultCsvUrl)
        ]);
        const localPuppyData = parsePuppyCSV(puppyText);
        const localAdultData = parseAdultCSV(adultText);

        bindCalculator(container, localPuppyData, localAdultData, localBeefPrices, localNonBeefPrices, mode);
      } catch (err) {
        console.error('Calculator init error:', err);
        container.innerHTML = '<p class="calculator-error">Unable to load feeding guide. Please try again later.</p>';
      }
    });
  }

  // ─── Bind a single calculator container ──────────────────────────────────
  function bindCalculator(container, puppyDataLocal, adultDataLocal, beefPricesLocal, nonBeefPricesLocal, mode) {

    // DOM references scoped to this container
    const ageBtns        = container.querySelectorAll('.feeding-calculator-dog-age-button-container a');
    const weightAdult    = container.querySelector('#ddlExpectedBodyWeightAdult');
    const weightPuppy    = container.querySelector('#ddlExpectedBodyWeightPuppy');
    const activitySelect = container.querySelector('#ddlAdultAge');
    const puppyAgeSelect = container.querySelector('#ddlPuppyAge');
    const tubSelect      = container.querySelector('#ddlProductWeightClass');
    const resultEl       = container.querySelector('.feeding-guide-result');
    const priceEl        = container.querySelector('.feeding-guide-price');
    const dashEl         = container.querySelector('.dash_calculate');

    const adultLabels    = container.querySelectorAll('.feeding-calc-adult');
    const puppyLabels    = container.querySelectorAll('.feeding-calc-Age');

    // ── Tab switching ────────────────────────────────────────────────────
    ageBtns.forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopImmediatePropagation();

        ageBtns.forEach(function(b) { b.classList.remove('active'); });
        btn.classList.add('active');

        const age = btn.dataset.doggyAge;

        if (age === 'pup') {
          weightAdult && weightAdult.closest('.calc-weight-input-wrap') && (weightAdult.closest('.calc-weight-input-wrap').style.display = 'none');
          const puppyWrap = container.querySelector('#puppyWeightWrap');
          puppyWrap && (puppyWrap.style.display = '');
          activitySelect && (activitySelect.style.display = 'none');
          puppyAgeSelect && (puppyAgeSelect.style.display = '');
          adultLabels.forEach(function(el) { el.style.display = 'none'; });
          puppyLabels.forEach(function(el) { el.style.display = ''; });
        } else {
          const puppyWrap = container.querySelector('#puppyWeightWrap');
          puppyWrap && (puppyWrap.style.display = 'none');
          weightAdult && weightAdult.closest('.calc-weight-input-wrap') && (weightAdult.closest('.calc-weight-input-wrap').style.display = '');
          puppyAgeSelect && (puppyAgeSelect.style.display = 'none');
          activitySelect && (activitySelect.style.display = '');
          puppyLabels.forEach(function(el) { el.style.display = 'none'; });
          adultLabels.forEach(function(el) { el.style.display = ''; });
        }

        updateResult();
      });
    });

    // ── Change + input listeners on all inputs/selects ──────────────────
    [weightAdult, weightPuppy, activitySelect, puppyAgeSelect, tubSelect].forEach(function(el) {
      if (el) {
        el.addEventListener('change', updateResult);
        el.addEventListener('input', updateResult); // fires on every keystroke for number inputs
      }
    });

    // ── Clamp weight inputs to 1–90 on blur ─────────────────────────────
    [weightAdult, weightPuppy].forEach(function(el) {
      if (el) {
        el.addEventListener('blur', function() {
          var val = parseInt(el.value);
          if (isNaN(val) || val < 1) el.value = 1;
          else if (val > 90) el.value = 90;
          updateResult();
        });
      }
    });

    // ── Core calculation ─────────────────────────────────────────────────
    function updateResult() {
      const activeBtn = container.querySelector('.feeding-calculator-dog-age-button-container a.active');
      const dogAge    = activeBtn ? activeBtn.dataset.doggyAge : 'adult';

      let dailyFeed = null;

      if (dogAge === 'pup') {
        const weight = weightPuppy ? parseInt(weightPuppy.value) : null;
        const month  = puppyAgeSelect ? parseInt(puppyAgeSelect.value) : null;
        if (weight && month && puppyDataLocal[weight] && puppyDataLocal[weight][month] !== undefined) {
          dailyFeed = puppyDataLocal[weight][month];
        }
      } else {
        const weight   = weightAdult ? parseInt(weightAdult.value) : null;
        const activity = activitySelect ? activitySelect.value : null;
        if (weight && activity && adultDataLocal[weight] && adultDataLocal[weight][activity] !== undefined) {
          dailyFeed = adultDataLocal[weight][activity];
        }
      }

      if (dailyFeed === null || isNaN(dailyFeed)) {
        if (resultEl) resultEl.textContent = '-';
        if (priceEl)  priceEl.textContent  = '-';
        if (dashEl)   dashEl.style.display = '';
        return;
      }

      if (resultEl) resultEl.textContent = parseFloat(dailyFeed).toFixed(2) + 'g';
      if (dashEl)   dashEl.style.display = 'none';

      const tubSize = tubSelect ? tubSelect.value : null;
      if (!tubSize) {
        if (priceEl) priceEl.textContent = '-';
        return;
      }

      const monthlyFeed = parseFloat(dailyFeed) * 28;
      const units       = monthlyFeed / parseFloat(tubSize);
      const priceBeef    = units * (beefPricesLocal[tubSize] || 0);
      const priceNonBeef = units * (nonBeefPricesLocal[tubSize] || 0);

      if (priceEl) {
        priceEl.textContent = 'R' + parseFloat(priceNonBeef).toFixed(2) + ' – R' + parseFloat(priceBeef).toFixed(2);
      }
    }

    // ── Auto-select puppy tab on puppy product pages ──────────────────
    if (window.location.href.toLowerCase().indexOf('puppy') !== -1) {
      const puppyBtn = container.querySelector('.feeding-calculator-dog-age-button-container a[data-doggy-age="pup"]');
      if (puppyBtn) puppyBtn.click();
    }

    // Initial render
    updateResult();
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
